/**
 * Checkout-cancel context — a one-shot sessionStorage breadcrumb that lets the
 * shared `/billing/cancel` page tell which checkout flow was abandoned.
 *
 * Stripe's `cancel_url` carries no proof and the cancel page is public, so the
 * only reliable signal is one the same browser leaves for itself right before
 * redirecting to the hosted Stripe page.
 *
 * Flow:
 *  - A checkout call site writes the kind via `setCheckoutCancelContext` just
 *    before redirecting to Stripe.
 *  - The cancel page reads it once via `readCheckoutCancelContext`, which also
 *    clears it — so a later cancel never inherits a stale label.
 *  - Writers always overwrite, the reader always clears: the cancel page is only
 *    reachable straight after a redirect, so the latest write is the truth.
 *
 * @layer-1 — shared lib (no React, no feature imports)
 */

const STORAGE_KEY = "un:checkoutCancelContext";

/** Which checkout flow a cancel belongs to. Defaults to "plan" when unknown. */
export type CheckoutCancelKind = "plan" | "service";

/** Record which flow is about to redirect to Stripe. Call before the redirect. */
export function setCheckoutCancelContext(kind: CheckoutCancelKind): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, kind);
  } catch {
    // Storage disabled/full (private-mode quirks) — the cancel page falls back
    // to "plan", the safe public default. Non-fatal.
  }
}

/** Read the flow once and clear it. Returns "plan" when absent or unreadable. */
export function readCheckoutCancelContext(): CheckoutCancelKind {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    return raw === "service" ? "service" : "plan";
  } catch {
    return "plan";
  }
}
